import classNames from 'classnames';
import { Data, Layout, Modal, UI, Utils } from 'common';
import { useFlags } from 'launchdarkly-react-client-sdk';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import propTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import MultipleTransactionDetails from './details/MultipleTransactionDetails.component';
import SingleTransactionDetails from './details/SingleTransactionDetails.component';
import PrintReceipt from './printReceipt/PrintReceipt.component';
import { RefundType } from './shared/RefundTypeOptions';
import { PrintStatus } from './shared/StatusOptions';
import { TrackingNumberType } from './shared/TrackingNumberType.enum';
import HistoryCommunication from './utils/HistoryCommunication';
import HistoryUtil from './utils/Util';

const { WrappedSplitButton, WrappedButtonType } = UI;
const { LayoutContext, RegionType } = Layout;
const { Carrier, ServiceType, Capability } = Data;
const { ModalContext } = Modal;
const { Util, Session, PrintUtil } = Utils;

const Details = ({
	selectedPrints,
	onClickCreateReturnLabelButton,
	onEmailTrackingInfoClick,
	costCodeOptions,
	setPrintsCostCode,
	onClickManageCostCodes,
	onFileClaimClick,
	onCheckClaimStatusClick,
	showUpgradeModal,
}) => {
	const { collapseSidePanel } = useContext(LayoutContext);
	const { showError } = useContext(ModalContext);

	// check if it's a single or multiple selection
	const selectionCount = isArray(selectedPrints) ? selectedPrints.length : 0;
	const isSingleSelection = selectionCount === 1;
	const selectedPrint = isSingleSelection ? selectedPrints[0] : null;
	const transactionDate = selectedPrint?.transactionDate;

	// get selected print's details
	const {
		carrier,
		eligibleForReturnLabel,
		insuredValue,
		insuranceType,
		printStatus,
		printTypeId,
		netstampSummary,
		serviceType,
		toAddress,
		refundType,
		trackingNumber,
		trackingNumberType,
		labelId,
		advancedOptionsNames,
		printTypeName,
	} =
		isSingleSelection && isObject(selectedPrint?.printDetails)
			? selectedPrint.printDetails
			: {};

	const reprintEligibility = HistoryUtil.getEligibilityForReprint(
		printStatus,
		carrier,
		transactionDate,
		printTypeName
	);

	const [labelAndOrderDetails, setOrderAndLabelDetails] = useState([]);
	const [showPrintReceipt, setShowPrintReceipt] = useState(false);
	const [disableReprintButton, setDisableReprintButton] = useState(true);

	// Hide the reprint button for WinClient if any of the following conditions are met:
	// 1. Connect is required.
	// 2. The Launch Darkly flag is not enabled.
	// 3. App Cap is not enabled
	const { IsInWinClient } = Session.getSettings();
	const hideReprintForWinClient =
		IsInWinClient && PrintUtil.isPluginRequired(selectedPrints);
	const reprintFromHistoryAppCap = Session.hasCapability(
		'reprint_from_history'
	);
	const { reprintFromHistory: reprintLDFlag = false } = useFlags();
	const isContainerLabel =
		labelAndOrderDetails?.labelDetails?.isContainerLabel === true;
	const hideReprint =
		!reprintLDFlag || !reprintFromHistoryAppCap || isContainerLabel;

	// calculate features visibility and disabled state
	const features = {
		// calculate the state for "Create Return Label"
		returnLabel: {
			visible: HistoryUtil.showTracking(trackingNumber, trackingNumberType),
			disabled:
				eligibleForReturnLabel !== true ||
				printStatus === PrintStatus.Refunded ||
				carrier === Carrier.DHLExpress,
		},

		// calculate the state for "email Tracking Info"
		emailTrackingInfo: {
			visible: !(
				trackingNumberType === TrackingNumberType.None ||
				trackingNumberType === TrackingNumberType.Imb ||
				carrier === Carrier.UPS ||
				carrier === Carrier.DHLExpress
			),

			// TODO: remove Certified Mail check once server side is fixed
			disabled:
				(refundType === RefundType.MailIn &&
					!advancedOptionsNames.includes('Certified Mail')) ||
				carrier === Carrier.DHLExpress,
		},

		// calculate the state for "track On USPS"
		trackOnUSPS: {
			visible: !(
				trackingNumberType === TrackingNumberType.None ||
				trackingNumberType === TrackingNumberType.Imb ||
				carrier === Carrier.UPS ||
				carrier === Carrier.DHLExpress
			),
		},

		// calculate the state for "print Receipt"
		printReceipt: {
			visible: carrier === Carrier.USPS,
		},

		// calculate the state for "file Claim refund"
		fileClaim: {
			visible:
				(isNumber(insuredValue) && insuredValue > 0) ||
				labelAndOrderDetails.labelDetails?.gapClaimMaxAmount === null ||
				carrier === Carrier.DHLExpress ||
				carrier === Carrier.FedEx,
			disabled:
				printStatus === PrintStatus.Refunded || carrier === Carrier.DHLExpress,
		},

		// calculate the state for "Check Claim Status"
		checkClaimStatus: {
			visible: HistoryUtil.showCheckClaimStatus(insuredValue, insuranceType),
		},

		// calculate the state for "refund Late Delivery"
		refundLateDelivery: {
			visible:
				serviceType === ServiceType.UspsPriorityMailExpress &&
				printStatus !== PrintStatus.Printed &&
				printStatus !== PrintStatus.Pending &&
				printStatus !== PrintStatus.Refunded,
		},

		// calculate the state for "reprint"
		reprint: {
			visible: !hideReprintForWinClient && !hideReprint,
			disabled: !reprintEligibility.isEligible || disableReprintButton,
			ineligibleForReprintReason: reprintEligibility.ineligibilityReason,
		},
	};

	const canPrintReturnLabel = Session.hasCapability(
		Capability.HistoryReturnLabel
	);
	const showUpgradeIconReturnLabel =
		!canPrintReturnLabel && !features.returnLabel.disabled;

	// build more button's menu items based on calculated features
	const moreButtons = [
		isSingleSelection && features.returnLabel.visible
			? {
					label: 'Create Return Label',
					icon: showUpgradeIconReturnLabel ? 'upgrade-icon' : null,
					command: () => {
						if (features.returnLabel.disabled) {
							return;
						}

						if (onClickCreateReturnLabelButton) {
							onClickCreateReturnLabelButton('historyPageDetailsPanel');
						}
					},
					disabled: features.returnLabel.disabled,
					dataqa: 'print-details-create-return-label-button',
				}
			: null,
		isSingleSelection && features.emailTrackingInfo.visible
			? {
					label: 'Email Tracking Info',
					command: () => onEmailTrackingInfoClick(),
					disabled: features.emailTrackingInfo.disabled,
					dataqa: 'print-details-email-tracking-label-button',
				}
			: null,
		isSingleSelection && features.trackOnUSPS.visible
			? {
					label: 'Track on USPS.com',
					command: () => {
						if (trackingNumber) {
							window.open(
								`https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`
							);
						}
					},
					dataqa: 'print-details-track-on-usps-button',
				}
			: null,
		isSingleSelection && features.printReceipt.visible
			? {
					label: 'Print Receipt',
					command: () => {
						setShowPrintReceipt(true);
						setTimeout(function () {
							setShowPrintReceipt(false);
						}, 100);
					},
					dataqa: 'print-details-print-receipt-button',
				}
			: null,
		isSingleSelection && features.fileClaim.visible
			? {
					label: 'File Claim',
					command: () => {
						if (onFileClaimClick) {
							onFileClaimClick();
						}
					},
					disabled: features.fileClaim.disabled,
					dataqa: 'print-details-file-claim-button',
				}
			: null,
		isSingleSelection && features.checkClaimStatus.visible
			? {
					label: 'Check Claim Status',
					command: () => {
						if (onCheckClaimStatusClick) {
							onCheckClaimStatusClick();
						}
					},
					dataqa: 'print-details-check-claim-status-button',
				}
			: null,
		{
			label: 'Collapse Panel',
			command: () => collapseSidePanel(RegionType.East),
			dataqa: 'print-details-collapse-panel-button',
		},
	].filter(btn => btn);

	/**
	 * Make a call to getLabelDetails API to fetch data.
	 * @param {string} labelId
	 * @return {object}
	 */
	async function fetchLabelDetails() {
		if (labelId !== undefined) {
			let labelDetails = {};
			let printDetails = {};

			// reset component state so that data does not persist between selections
			setOrderAndLabelDetails(labelDetails, printDetails);
			setDisableReprintButton(true);

			const response = await HistoryCommunication.getLabelDetails(labelId);

			if (HistoryCommunication.isSuccessful(response)) {
				labelDetails = response.data.label_details;
				printDetails = response.data.print_details;
			} else {
				showError({ response });
			}

			setOrderAndLabelDetails(Util.toCamelCase({ printDetails, labelDetails }));
			setDisableReprintButton(false);
		}
	}

	useEffect(() => {
		fetchLabelDetails();
	}, [labelId]);

	return (
		<div className="history history-details">
			<div className="header history-header-panel">
				<div className="title">
					{isSingleSelection
						? `${printTypeId === 5 ? netstampSummary : toAddress?.name}`
						: `${selectedPrints.length} Records Selected`}
				</div>
				<WrappedSplitButton
					icon="more-icon"
					menuClassName={
						showUpgradeIconReturnLabel ? 'view-more-options-menu' : ''
					}
					buttonType={WrappedButtonType.Icon}
					tooltip="View more options."
					tooltipOptions={{ position: 'left' }} // Forcing position here as there is a bug in Prime React WEBAPPS-36845
					model={moreButtons}
					dataqa="print-details-view-more-options"
				/>
			</div>
			{isSingleSelection && labelAndOrderDetails ? (
				<SingleTransactionDetails
					selectedPrint={selectedPrint}
					labelAndOrderDetails={labelAndOrderDetails}
					features={features}
					costCodeOptions={costCodeOptions}
					onClickCreateReturnLabelButton={onClickCreateReturnLabelButton}
					onEmailTrackingInfoClick={onEmailTrackingInfoClick}
					setPrintsCostCode={setPrintsCostCode}
					onClickManageCostCodes={onClickManageCostCodes}
					onFileClaimClick={onFileClaimClick}
					onCheckClaimStatusClick={onCheckClaimStatusClick}
					showUpgradeModal={showUpgradeModal}
				/>
			) : (
				<MultipleTransactionDetails selectedPrints={selectedPrints} />
			)}
			{showPrintReceipt && isSingleSelection && (
				<PrintReceipt
					selectedPrint={selectedPrint}
					labelDetail={labelAndOrderDetails}
				/>
			)}
		</div>
	);
};

Details.propTypes = {
	selectedPrints: propTypes.array.isRequired,
};

export default Details;
